import React from 'react';
import { Icon } from '../../components';
import { useIdentity } from '../../hooks/useIdentity';
import { useRoomHistory, mergeCurrentParty } from '../../hooks/useRoomHistory';
import { GLOB } from '../../router/paths';
import { formatRelative } from '../../lib/relativeTime';
import './RoomsList.css';

// Sub-line for a party row: relative last-activity ("active 4m ago" /
// "silent · 3h") when we know it, a neutral "private party" otherwise.
function partySub(lastActive) {
  const rel = formatRelative(lastActive);
  if (!rel) return 'private party';
  return rel === 'just now' ? 'active just now' : `active ${rel}`;
}

const Row = ({ room, active, onSelect }) => {
  const handle = () => onSelect && onSelect(room.code);
  return (
    <button
      type="button"
      className={`rooms-list__row${active ? ' rooms-list__row--active' : ''}`}
      onClick={handle}
      aria-current={active ? 'page' : undefined}
      aria-label={`Switch to room ${room.code}`}
    >
      <span className="rooms-list__icon" aria-hidden="true">
        <Icon name={room.icon} size={16} />
      </span>
      <span className="rooms-list__body">
        <span className={`rooms-list__name${room.icon === 'lock' ? ' rooms-list__name--code' : ''}`}>
          {room.code}
        </span>
        <span className="rooms-list__sub">{room.sub}</span>
      </span>
    </button>
  );
};

/**
 * RoomsList — desktop sidebar list. #GLOB is always pinned at the top, then
 * every party the visitor has interacted in (sent a message in), sourced from
 * useRoomHistory — a single per-tab-session backend query, cached. The party
 * the visitor is currently in is always shown even before they've messaged
 * there (mergeCurrentParty), and is rendered as the active row.
 */
const RoomsList = ({ onSelectRoom }) => {
  const { partyCode, isInGlob } = useIdentity();
  const { rooms: history } = useRoomHistory();
  const activeCode = isInGlob ? GLOB : partyCode;

  const parties = mergeCurrentParty(history, partyCode, isInGlob);

  const rows = [
    { code: GLOB, icon: 'globe', sub: 'the global room' },
    ...parties.map((p) => ({
      code: p.code,
      icon: 'lock',
      sub: partySub(p.lastActive),
    })),
  ];

  return (
    <nav className="rooms-list" aria-label="Rooms">
      <div className="rooms-list__heading">Rooms</div>
      {rows.map((r) => (
        <Row key={r.code} room={r} active={r.code === activeCode} onSelect={onSelectRoom} />
      ))}
    </nav>
  );
};

export default RoomsList;
